import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  RotateLeft as RotateLeftIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { useUpdateQueryParams } from 'commons/components/SearchRelated/SearchWithCriteria/hooks';
import { PRIMARY_GRAY } from 'commons/styles/colors';
import { IDeliveryNote } from 'commons/types';
import {
  getDeliveryNoteListAction,
  getDeliveryNotePriorityListAction,
} from 'redux-stores/actions/deliveryNote.action';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setSelectedDeliveryNote } from 'redux-stores/reducers/deliveryNoteReducer';
import { AppDispatch } from 'redux-stores/store';
import { DELIVERY_NOTE_ROUTES } from 'routes/constants';

import {
  DELIVERY_NOTE_LIST_DEFAULT_PAGE_SIZE,
  DELIVERY_NOTE_STATUS_DISPLAY,
  DELIVERY_NOTE_LIST_MOBILE_BREAKPOINT,
  DELIVERY_NOTE_STATUS_BADGE_COLORS,
} from './DNListPage.constants';
import S from './DNListPage.styles';

const DNListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { deliveryNotes, totalDeliveryNotes, priorityList } = useSelector(
    (state: RootReducerInterface) => state.deliveryNote,
  );

  const [searchParams] = useSearchParams({
    page: '1',
    page_size: `${DELIVERY_NOTE_LIST_DEFAULT_PAGE_SIZE}`,
  });
  const { updateQueryParams } = useUpdateQueryParams();

  const isMobile = useMediaQuery(
    `(max-width: ${DELIVERY_NOTE_LIST_MOBILE_BREAKPOINT}px)`,
  );

  const [awbNumber, setAWBNumber] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const fetchDeliveryNotes = useDebouncedCallback(
    (
      _searchParams: URLSearchParams,
      _awbNumber: string,
      _priorityFilter: string,
      _dispatch: AppDispatch,
    ): void => {
      const queries = new URLSearchParams(_searchParams);
      if (_awbNumber) queries.set('awb_number', _awbNumber);
      if (_priorityFilter !== 'all') queries.set('priority', _priorityFilter);
      _dispatch(getDeliveryNoteListAction({ searchParams: queries }));
    },
    200,
    { maxWait: 1000 },
  );

  useEffect(() => {
    dispatch(getDeliveryNotePriorityListAction());
  }, [dispatch]);

  useEffect(() => {
    fetchDeliveryNotes(searchParams, awbNumber, priorityFilter, dispatch);
  }, [fetchDeliveryNotes, searchParams, awbNumber, priorityFilter, dispatch]);

  const totalItems = totalDeliveryNotes;
  const totalPages = Math.max(
    1,
    Math.ceil(totalItems / DELIVERY_NOTE_LIST_DEFAULT_PAGE_SIZE),
  );

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setAWBNumber(event.target.value);
    updateQueryParams({ page: '1' });
  };

  const handlePriorityChange = (event: SelectChangeEvent<string>): void => {
    setPriorityFilter(event.target.value);
    updateQueryParams({ page: '1' });
  };

  const handleRefresh = (): void => {
    setAWBNumber('');
    setPriorityFilter('all');
    updateQueryParams({ page: '1' });
    fetchDeliveryNotes(
      new URLSearchParams({
        page: '1',
        page_size: `${DELIVERY_NOTE_LIST_DEFAULT_PAGE_SIZE}`,
      }),
      '',
      'all',
      dispatch,
    );
  };

  const handleNavigateToDetail = (deliveryNote: IDeliveryNote): void => {
    dispatch(setSelectedDeliveryNote(deliveryNote));
    navigate(DELIVERY_NOTE_ROUTES.DETAIL);
  };

  const handlePrevPage = (): void => {
    updateQueryParams({ page: `${currentPage - 1}` });
  };

  const handleNextPage = (): void => {
    updateQueryParams({ page: `${currentPage + 1}` });
  };

  const showingStart =
    totalItems === 0
      ? 0
      : (currentPage - 1) * DELIVERY_NOTE_LIST_DEFAULT_PAGE_SIZE + 1;
  const showingEnd = Math.min(
    currentPage * DELIVERY_NOTE_LIST_DEFAULT_PAGE_SIZE,
    totalItems,
  );

  return (
    <S.PageWrapper>
      <S.FilterRow>
        <S.SearchField
          placeholder={isMobile ? 'AWB Number' : 'Search by AWB Number...'}
          variant="outlined"
          size="small"
          value={awbNumber}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: isMobile ? null : (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: awbNumber ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setAWBNumber('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            label="Priority"
            value={priorityFilter}
            onChange={handlePriorityChange}
          >
            <MenuItem value="all">All</MenuItem>
            {priorityList.map((priority) => (
              <MenuItem key={priority} value={priority}>
                {priority}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <S.RefreshButton
          startIcon={<RotateLeftIcon />}
          onClick={handleRefresh}
          sx={{ ml: 'auto' }}
        >
          Refresh
        </S.RefreshButton>
      </S.FilterRow>

      <S.TableWrapper>
        <S.StyledTable>
          <thead>
            <tr>
              <S.TableHeaderCell>AWB Number</S.TableHeaderCell>
              <S.TableHeaderCell>Cust ID</S.TableHeaderCell>
              <S.TableHeaderCell>Priority</S.TableHeaderCell>
              <S.TableHeaderCell>Total Orders</S.TableHeaderCell>
              <S.TableHeaderCell>Status</S.TableHeaderCell>
              <S.TableHeaderCell>Actions</S.TableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {deliveryNotes.map((dn) => (
              <S.DataRow key={dn.awb_number}>
                <S.DataCell>{dn.awb_number}</S.DataCell>
                <S.DataCell>{dn.pharmacy_id}</S.DataCell>
                <S.DataCell>{dn.priority ?? '-'}</S.DataCell>
                <S.DataCell>{dn.picking_task_count}</S.DataCell>
                <S.DataCell>
                  <S.StatusBadge
                    $bg={
                      DELIVERY_NOTE_STATUS_BADGE_COLORS[dn.status]?.bg ??
                      '#E5E5E5'
                    }
                    $color={
                      DELIVERY_NOTE_STATUS_BADGE_COLORS[dn.status]?.color ??
                      '#525252'
                    }
                  >
                    {DELIVERY_NOTE_STATUS_DISPLAY[dn.status] ?? dn.status}
                  </S.StatusBadge>
                </S.DataCell>
                <S.DataCell>
                  <S.DetailsButton onClick={() => handleNavigateToDetail(dn)}>
                    <S.DetailsButtonLabel>Details</S.DetailsButtonLabel>
                    <S.DetailsButtonIcon>
                      <ChevronRightIcon fontSize="small" />
                    </S.DetailsButtonIcon>
                  </S.DetailsButton>
                </S.DataCell>
              </S.DataRow>
            ))}
            {deliveryNotes.length === 0 && (
              <S.DataRow>
                <S.NoDataCell
                  colSpan={6}
                  style={{ textAlign: 'center', color: PRIMARY_GRAY }}
                >
                  No data found
                </S.NoDataCell>
              </S.DataRow>
            )}
          </tbody>
        </S.StyledTable>
      </S.TableWrapper>

      <S.PaginationRow>
        <S.PaginationText>
          Showing {showingStart} to {showingEnd} of {totalItems} items
        </S.PaginationText>
        <S.PaginationControls>
          <S.PaginationButton
            disabled={currentPage <= 1}
            onClick={handlePrevPage}
          >
            <ChevronLeftIcon />
          </S.PaginationButton>
          <S.PageNumber>{currentPage}</S.PageNumber>
          <S.PaginationButton
            disabled={currentPage >= totalPages}
            onClick={handleNextPage}
          >
            <ChevronRightIcon />
          </S.PaginationButton>
        </S.PaginationControls>
      </S.PaginationRow>
    </S.PageWrapper>
  );
};

export default DNListPage;
